import type { V2 } from '../core/math';
import type { WorldGeom } from './body';

/**
 * Narrowphase. Produces a contact manifold with the normal pointing from A to B and
 * up to two contact points (Box2D-lite style reference/incident face clipping for boxes).
 */
export interface ContactPoint {
  x: number;
  z: number;
  depth: number;
}
export interface Manifold {
  nx: number;
  nz: number;
  count: number;
  points: [ContactPoint, ContactPoint];
}

export function makeManifold(): Manifold {
  return { nx: 0, nz: 0, count: 0, points: [{ x: 0, z: 0, depth: 0 }, { x: 0, z: 0, depth: 0 }] };
}

export function aabbOverlap(a: WorldGeom, b: WorldGeom): boolean {
  return a.aabb.minX <= b.aabb.maxX && a.aabb.maxX >= b.aabb.minX && a.aabb.minZ <= b.aabb.maxZ && a.aabb.maxZ >= b.aabb.minZ;
}

/** Returns true and fills `m` if the shapes overlap. */
export function collide(a: WorldGeom, b: WorldGeom, m: Manifold): boolean {
  m.count = 0;
  if (a.isCircle && b.isCircle) return circleCircle(a, b, m);
  if (!a.isCircle && b.isCircle) return polyCircle(a, b, m, false);
  if (a.isCircle && !b.isCircle) return polyCircle(b, a, m, true);
  return polyPoly(a, b, m);
}

function circleCircle(a: WorldGeom, b: WorldGeom, m: Manifold): boolean {
  const dx = b.center.x - a.center.x;
  const dz = b.center.z - a.center.z;
  const r = a.radius + b.radius;
  const d2 = dx * dx + dz * dz;
  if (d2 > r * r) return false;
  const d = Math.sqrt(d2);
  let nx = 1;
  let nz = 0;
  if (d > 1e-9) {
    nx = dx / d;
    nz = dz / d;
  }
  m.nx = nx;
  m.nz = nz;
  const p = m.points[0];
  p.depth = r - d;
  p.x = a.center.x + nx * (a.radius - p.depth * 0.5);
  p.z = a.center.z + nz * (a.radius - p.depth * 0.5);
  m.count = 1;
  return true;
}

/** Polygon `p` vs circle `c`. If `flip`, the manifold is written as circle(A) vs poly(B). */
function polyCircle(p: WorldGeom, c: WorldGeom, m: Manifold, flip: boolean): boolean {
  const cx = c.center.x;
  const cz = c.center.z;
  const r = c.radius;
  let sep = -Infinity;
  let edge = 0;
  for (let i = 0; i < 4; i++) {
    const n = p.normals[i] as V2;
    const v = p.verts[i] as V2;
    const s = n.x * (cx - v.x) + n.z * (cz - v.z);
    if (s > r) return false;
    if (s > sep) {
      sep = s;
      edge = i;
    }
  }
  const v1 = p.verts[edge] as V2;
  const v2 = p.verts[(edge + 1) % 4] as V2;
  let nx: number;
  let nz: number;
  let depth: number;
  if (sep < 1e-9) {
    // Centre inside polygon.
    const n = p.normals[edge] as V2;
    nx = n.x;
    nz = n.z;
    depth = r - sep;
  } else {
    const u1 = (cx - v1.x) * (v2.x - v1.x) + (cz - v1.z) * (v2.z - v1.z);
    const u2 = (cx - v2.x) * (v1.x - v2.x) + (cz - v2.z) * (v1.z - v2.z);
    if (u1 <= 0 || u2 <= 0) {
      const v = u1 <= 0 ? v1 : v2;
      const dx = cx - v.x;
      const dz = cz - v.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > r * r) return false;
      const d = Math.sqrt(d2) || 1e-9;
      nx = dx / d;
      nz = dz / d;
      depth = r - d;
    } else {
      const n = p.normals[edge] as V2;
      nx = n.x;
      nz = n.z;
      depth = r - sep;
    }
  }
  const pt = m.points[0];
  pt.x = cx - nx * r;
  pt.z = cz - nz * r;
  pt.depth = depth;
  m.count = 1;
  if (flip) {
    m.nx = -nx;
    m.nz = -nz;
  } else {
    m.nx = nx;
    m.nz = nz;
  }
  return true;
}

function maxSeparation(a: WorldGeom, b: WorldGeom, out: { sep: number; edge: number }): void {
  let best = -Infinity;
  let bestEdge = 0;
  for (let i = 0; i < 4; i++) {
    const n = a.normals[i] as V2;
    const v = a.verts[i] as V2;
    let min = Infinity;
    for (let j = 0; j < 4; j++) {
      const w = b.verts[j] as V2;
      const d = n.x * (w.x - v.x) + n.z * (w.z - v.z);
      if (d < min) min = d;
    }
    if (min > best) {
      best = min;
      bestEdge = i;
    }
  }
  out.sep = best;
  out.edge = bestEdge;
}

const sepA = { sep: 0, edge: 0 };
const sepB = { sep: 0, edge: 0 };
const clip1: ContactPoint[] = [
  { x: 0, z: 0, depth: 0 },
  { x: 0, z: 0, depth: 0 },
];
const clip2: ContactPoint[] = [
  { x: 0, z: 0, depth: 0 },
  { x: 0, z: 0, depth: 0 },
];

function clipSegment(vin: ContactPoint[], out: ContactPoint[], nx: number, nz: number, offset: number): number {
  let num = 0;
  const v0 = vin[0] as ContactPoint;
  const v1 = vin[1] as ContactPoint;
  const d0 = nx * v0.x + nz * v0.z - offset;
  const d1 = nx * v1.x + nz * v1.z - offset;
  if (d0 <= 0) {
    const o = out[num++] as ContactPoint;
    o.x = v0.x;
    o.z = v0.z;
  }
  if (d1 <= 0) {
    const o = out[num++] as ContactPoint;
    o.x = v1.x;
    o.z = v1.z;
  }
  if (d0 * d1 < 0 && num < 2) {
    const t = d0 / (d0 - d1);
    const o = out[num++] as ContactPoint;
    o.x = v0.x + t * (v1.x - v0.x);
    o.z = v0.z + t * (v1.z - v0.z);
  }
  return num;
}

function polyPoly(a: WorldGeom, b: WorldGeom, m: Manifold): boolean {
  maxSeparation(a, b, sepA);
  if (sepA.sep > 0) return false;
  maxSeparation(b, a, sepB);
  if (sepB.sep > 0) return false;

  let ref: WorldGeom;
  let inc: WorldGeom;
  let edge: number;
  let flip: boolean;
  if (sepB.sep > sepA.sep * 0.98 + 0.001) {
    ref = b;
    inc = a;
    edge = sepB.edge;
    flip = true;
  } else {
    ref = a;
    inc = b;
    edge = sepA.edge;
    flip = false;
  }
  const rn = ref.normals[edge] as V2;
  // Incident edge: most anti-parallel to the reference normal.
  let incEdge = 0;
  let minDot = Infinity;
  for (let i = 0; i < 4; i++) {
    const n = inc.normals[i] as V2;
    const d = n.x * rn.x + n.z * rn.z;
    if (d < minDot) {
      minDot = d;
      incEdge = i;
    }
  }
  const i1 = inc.verts[incEdge] as V2;
  const i2 = inc.verts[(incEdge + 1) % 4] as V2;
  const incident = clip1;
  (incident[0] as ContactPoint).x = i1.x;
  (incident[0] as ContactPoint).z = i1.z;
  (incident[1] as ContactPoint).x = i2.x;
  (incident[1] as ContactPoint).z = i2.z;

  const v11 = ref.verts[edge] as V2;
  const v12 = ref.verts[(edge + 1) % 4] as V2;
  let tx = v12.x - v11.x;
  let tz = v12.z - v11.z;
  const tl = Math.sqrt(tx * tx + tz * tz) || 1;
  tx /= tl;
  tz /= tl;
  const sideOffset1 = -(tx * v11.x + tz * v11.z);
  const sideOffset2 = tx * v12.x + tz * v12.z;

  let np = clipSegment(incident, clip2, -tx, -tz, sideOffset1);
  if (np < 2) return false;
  np = clipSegment(clip2, clip1, tx, tz, sideOffset2);
  if (np < 2) return false;

  const frontOffset = rn.x * v11.x + rn.z * v11.z;
  let count = 0;
  for (let i = 0; i < 2; i++) {
    const cp = clip1[i] as ContactPoint;
    const sep = rn.x * cp.x + rn.z * cp.z - frontOffset;
    if (sep <= 0) {
      const p = m.points[count++] as ContactPoint;
      // Place the point midway between the two surfaces.
      p.x = cp.x - rn.x * sep * 0.5;
      p.z = cp.z - rn.z * sep * 0.5;
      p.depth = -sep;
    }
  }
  if (count === 0) return false;
  m.count = count;
  m.nx = flip ? -rn.x : rn.x;
  m.nz = flip ? -rn.z : rn.z;
  return true;
}
